'use client';
import { useComments } from '@/hooks/api/comments';
import { useRef, useState } from 'react';
import CommentCard from './CommentCard';
import EmptyContent from '../common/EmptyContent';
import {
  Box,
  Stack,
  Typography,
  Button,
  useTheme,
  Skeleton,
  Pagination,
} from '@mui/material';
import { FaRegCommentAlt } from 'react-icons/fa';
import BaseDialog from '../dialogs/BaseDialog';
import useDialog from '@/hooks/dialog';
import CommentDialog from '../dialogs/CommentDialog';
import { BiCommentAdd } from 'react-icons/bi';
import PinnedComments from './PinnedComments';
import { useUserStore } from '@/store/auth';

const Comments = ({
  id,
  hasBuy = false,
  courseOwner = false,
  blogOwner = false,
  type = 'course',
}) => {
  const theme = useTheme();
  const limit = 8;
  const [pageIndex, setPageIndex] = useState(1);
  const { data, isLoading, error, isError, refetch } = useComments({
    limit,
    currentPage: pageIndex,
    post: id,
  });
  const auth = useUserStore(state => state.auth);
  const modalRef = useRef(null);
  const { openModal, closeModal } = useDialog(modalRef);

  const handleRefetchData = () => {
    if (pageIndex === 1) {
      refetch();
    } else {
      setPageIndex(1);
    }
  };

  if (isLoading)
    return (
      <>
        <Skeleton variant="rounded" height={200} sx={{ mb: 5 }} />
      </>
    );

  if (isError) {
    return (
      <EmptyContent sx={{ height: '100%' }}>
        <Typography>خطایی رخ داده است</Typography>
      </EmptyContent>
    );
  }
  return (
    <>
      {data.results?.length > 0 ? (
        <Stack gap={4} my={5}>
          <Box>
            <PinnedComments
              id={id}
              type={type}
              handleRefetchData={handleRefetchData}
              courseOwner={courseOwner}
              blogOwner={blogOwner}
            />
          </Box>
          <Typography fontSize={24} fontWeight={500} color="text.secondary">
            {type === 'course' || type === 'blog' ? 'نظرات' : 'پرسش و پاسخ'}
          </Typography>
          <Stack gap={3}>
            {data.results?.map((item, index) => (
              <CommentCard
                key={index}
                comment={item}
                id={id}
                handleRefetchData={handleRefetchData}
                type={type}
                courseOwner={courseOwner}
                blogOwner={blogOwner}
                maxPinCount={
                  type === 'blog'
                    ? data.generalInfo?.blog?.maxComment
                    : data.generalInfo?.course?.maxComment
                }
                canPin={
                  type === 'blog'
                    ? data.generalInfo?.blog?.countOfComment <
                      data.generalInfo?.blog?.maxComment
                    : data.generalInfo?.course?.countOfComment <
                      data.generalInfo?.course?.maxComment
                }
                hasBuy={hasBuy}
              />
            ))}
          </Stack>
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            {data.totalPages > 1 && (
              <Pagination
                hideNextButton
                hidePrevButton
                size="large"
                count={data.totalPages}
                color="primary"
                page={pageIndex}
                onChange={(event, value) => setPageIndex(value)}
                sx={{
                  '& .MuiPagination-ul': { flexWrap: 'nowrap' },
                }}
              />
            )}
          </Box>
          {auth() && (
            <Box sx={{ textAlign: 'center' }}>
              <Button
                onClick={openModal}
                variant="FAB"
                disabled={!hasBuy}
                startIcon={<BiCommentAdd style={{ marginLeft: '7px' }} />}>
                ثبت نظر
              </Button>
            </Box>
          )}
        </Stack>
      ) : (
        <EmptyContent sx={{ width: '100%', height: '100%' }}>
          <Stack alignItems={'center'} gap={3}>
            <FaRegCommentAlt
              size={50}
              color={`${theme.palette.primary.main}`}
            />
            <Typography
              variant="body2"
              textAlign="center"
              color="text.secondary">
              {type === 'course' || type === 'blog'
                ? 'هیچ نظری ثبت نشده است، اولین نظر رو ثبت کنید'
                : 'هیچ سوالی مطرح نشده است، سوالت رو بپرس'}
            </Typography>
            {auth() && (
              <Button
                onClick={openModal}
                variant="FAB"
                disabled={!hasBuy && type !== 'blog'}
                startIcon={<BiCommentAdd style={{ marginLeft: '7px' }} />}>
                {type === 'course' || type === 'blog' ? 'ثبت نظر' : 'طرح سوال'}
              </Button>
            )}
          </Stack>
        </EmptyContent>
      )}

      <BaseDialog
        ref={modalRef}
        title={type === 'course' || type === 'blog' ? 'ثبت نظر' : 'طرح سوال'}>
        <CommentDialog
          closeModal={closeModal}
          type={type}
          id={id}
          handleRefetchData={handleRefetchData}
        />
      </BaseDialog>
    </>
  );
};

export default Comments;
